import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { useCreateReminder, useUpdateReminder } from '@/features/finance/hooks/useReminders';
import type { ReminderConfig } from '@/services/reminderService';

interface ReminderFormModalProps {
  reminder: ReminderConfig | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ReminderFormModal: React.FC<ReminderFormModalProps> = ({
  reminder,
  onClose,
  onSuccess,
}) => {
  const isEditing = !!reminder;

  // Default message template
  const defaultMessageTemplate = `Bonjour {{studentName}},

Nous vous informons que vous avez un paiement en attente.

Montant dû: {{amount}} MAD
Date d'échéance: {{dueDate}}

Merci de régulariser votre situation dans les plus brefs délais.

Cordialement,
Le Service Financier`;

  const [formData, setFormData] = useState({
    name: reminder?.name || '',
    trigger: reminder?.trigger || 'BEFORE_DUE',
    daysOffset: reminder?.daysOffset ?? 0,
    messageTemplate: reminder?.messageTemplate || defaultMessageTemplate,
    isActive: reminder?.isActive ?? true,
  });

  // Separate string state for the number input to allow typing negative numbers
  const [daysOffsetInput, setDaysOffsetInput] = useState(String(formData.daysOffset));

  const createMutation = useCreateReminder();
  const updateMutation = useUpdateReminder();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    // Validate name field
    if (!formData.name.trim()) {
      setErrorMessage('Le nom de la relance est obligatoire');
      return;
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: reminder.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error saving reminder:', error);
      const message = error?.response?.data?.message || error?.message || 'Erreur lors de la sauvegarde';
      setErrorMessage(message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Modifier la relance' : 'Nouvelle relance'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="px-6 py-4 space-y-4">
              {/* Error Message */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <span className="text-red-500">⚠</span>
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la relance *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ex: Rappel 7 jours avant"
                />
              </div>

              {/* Trigger */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de déclenchement *
                </label>
                <select
                  required
                  value={formData.trigger}
                  onChange={(e) => setFormData({ ...formData, trigger: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="BEFORE_DUE">Avant échéance</option>
                  <option value="AFTER_DUE">Après échéance (jour J)</option>
                  <option value="OVERDUE_15">Retard 15 jours</option>
                  <option value="OVERDUE_30">Retard 30 jours</option>
                  <option value="OVERDUE_60">Retard 60 jours</option>
                  <option value="SUSPENSION_WARNING">Avertissement suspension</option>
                </select>
              </div>

              {/* Days Offset */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de jours (négatif = avant, positif = après) *
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="-?[0-9]*"
                  required
                  value={daysOffsetInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty, minus sign, or numbers (including negative)
                    if (value === '' || value === '-' || /^-?\d+$/.test(value)) {
                      setDaysOffsetInput(value);
                      // Update formData only when we have a valid number
                      const parsed = parseInt(value, 10);
                      if (!isNaN(parsed)) {
                        setFormData({ ...formData, daysOffset: parsed });
                      }
                    }
                  }}
                  onBlur={() => {
                    // On blur, ensure we have a valid number
                    const parsed = parseInt(daysOffsetInput, 10);
                    if (isNaN(parsed)) {
                      setDaysOffsetInput('0');
                      setFormData({ ...formData, daysOffset: 0 });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ex: -7 pour 7 jours avant, 0 pour jour J"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Exemple: -7 = 7 jours avant l'échéance, 0 = jour de l'échéance, +1 = 1 jour après
                </p>
              </div>

              {/* Message Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message de notification *
                </label>
                <textarea
                  required
                  value={formData.messageTemplate}
                  onChange={(e) => setFormData({ ...formData, messageTemplate: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Bonjour {{studentName}},&#10;&#10;Votre échéance de {{amount}} MAD est due le {{dueDate}}.&#10;&#10;Merci de régulariser votre situation."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Variables disponibles: <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{studentName}}'}</code>, <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{amount}}'}</code>, <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{dueDate}}'}</code>
                </p>
              </div>

              {/* Info: In-App Only */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  📱 Les relances seront envoyées en tant que notifications in-app aux étudiants concernés.
                </p>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Activer cette relance
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {(createMutation.isPending || updateMutation.isPending) && (
                  <Loader2 size={16} className="animate-spin" />
                )}
                <Save size={16} />
                {isEditing ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
